import { Briefcase, ChevronDown, Crown, Home, LogOut, Menu, Users, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useNavigation } from "../hooks/useNavigation";
import { UserMode } from "../types/navigation";
import httpService from "../services/httpService";
import IntercomChatButton from "./IntercomChatButton";
import NotificationBell from "./notifications/NotificationBell";
import AlphaModeControl from "./alpha/AlphaModeControl";
import { hasNavigationChildren, isNavigationChildActive, isNavigationItemActive } from "../utils/navigationItems";

interface MobileNavProps {
  /**
   * Force the nav mode regardless of route. Role-specific shells pass this so the mobile menu
   * matches the shell (e.g. provider shell wrapping the plural `/providers/:id/profile` route).
   */
  mode?: UserMode;
}

const MobileNav: React.FC<MobileNavProps> = ({ mode }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  // Collapsible groups (PWA-2748); an active group is always expanded.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [providerLogoUrl, setProviderLogoUrl] = useState<string | null>(null);
  const [loadingLogo, setLoadingLogo] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, userConfig } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/login");
    setIsOpen(false);
  };

  const handleNavigation = (path: string): void => {
    navigate(path);
    setIsOpen(false);
  };

  const userRoles = user?.userRoles?.map((ur: { role: string }) => ur.role) || [];
  const hasRole = (role: string) => userRoles.includes(role);
  const isAdmin = hasRole("ADMIN");
  const isProvider = hasRole("PROVIDER");
  const isAdvisor = hasRole("ADVISOR");
  const isInvestor = hasRole("INVESTOR") || userRoles.length === 0;

  const isIbkrConnected = Boolean(userConfig?.ibkrAccountId);

  const { menuItems, bottomMenuItems, isProviderMode, isAdvisorMode, isAdminMode } = useNavigation({
    isUserAdmin: isAdmin,
    modeOverride: mode,
  });

  useEffect(() => {
    const fetchProviderLogo = async () => {
      if (!isProviderMode || !user) {
        setProviderLogoUrl(null);
        return;
      }

      const providerId = user?.provider?.id || user?.userProviders?.[0]?.providerId;
      if (!providerId) {
        return;
      }

      try {
        setLoadingLogo(true);
        const response = await httpService.get(`/providers/${providerId}/logo`);
        if (response?.data?.logoUrl) {
          setProviderLogoUrl(response.data.logoUrl);
        } else {
          setProviderLogoUrl(null);
        }
      } catch (error) {
        setProviderLogoUrl(null);
      } finally {
        setLoadingLogo(false);
      }
    };

    fetchProviderLogo();
  }, [isProviderMode, user]);

  return (
    <>
      {/* Mobile Header */}
      <div className="md:hidden bg-rp-black border-b border-rp-separator px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] flex items-center justify-between relative z-20">
        <div className="flex items-center">
          <img src="/favicon white.png" alt="Plutus" className="h-8 w-8 mr-2" />
          <img src="/dark mode text.png" alt="Plutus" className="h-6 w-auto" />
          {isProviderMode && (
            <span className="ml-2 text-xs font-medium text-rp-amber">
              for Providers
            </span>
          )}
          {isAdvisorMode && (
            <span className="ml-2 text-xs font-medium text-rp-amber">
              for Advisors
            </span>
          )}
          {isAdminMode && (
            <span className="ml-2 text-xs font-medium text-red-400">Admin</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isAdminMode && <NotificationBell />}
          <IntercomChatButton />
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 rounded-lg hover:bg-rp-separator text-rp-grey transition-colors"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div className="md:hidden fixed inset-0 z-50 overflow-hidden">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setIsOpen(false)} />

          {/* Menu panel */}
          <div className="fixed inset-y-0 right-0 w-64 bg-rp-black border-l border-rp-separator shadow-xl flex flex-col pb-[env(safe-area-inset-bottom)]">
            {/* Header */}
            <div className="flex items-center justify-between p-4 pt-[max(1rem,env(safe-area-inset-top))] border-b border-rp-separator">
              <div className="flex items-center">
                <img src="/favicon white.png" alt="Plutus" className="h-8 w-8 mr-2" />
                <img src="/dark mode text.png" alt="Plutus" className="h-6 w-auto" />
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-lg hover:bg-rp-separator text-rp-grey transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable Navigation */}
            <div className="flex-1 overflow-y-auto flex flex-col">
              <div>
                {/* Provider Name and Logo */}
                {isProviderMode && (
                  <div className="px-6 pt-7 pb-6">
                    <div className="flex flex-col items-center">
                      <div className="flex-shrink-0">
                        {loadingLogo ? (
                          <div className="w-20 h-20 rounded-lg flex items-center justify-center bg-rp-separator">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-rp-amber"></div>
                          </div>
                        ) : providerLogoUrl ? (
                          <img
                            src={providerLogoUrl}
                            alt="Provider logo"
                            className="w-20 h-20 object-cover rounded-lg"
                            onError={e => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="w-20 h-20 rounded-lg flex items-center justify-center bg-rp-separator">
                            <Briefcase className="w-10 h-10 text-rp-amber" />
                          </div>
                        )}
                      </div>
                      <div className="w-full text-center">
                        <p className="text-xs font-medium text-rp-grey mt-2">
                          {user?.provider?.name || user?.userProviders?.[0]?.providers?.name || "Provider"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Menu Items */}
                <nav className="px-3 space-y-1 mt-4">
                  {menuItems.map(item => {
                    const Icon = item.icon;
                    const isGroup = hasNavigationChildren(item);
                    const isActive = isGroup ? isNavigationItemActive(item, location.pathname) : location.pathname === item.path;
                    const isOpen = isGroup && (isActive || Boolean(openGroups[item.id]));

                    return (
                      <React.Fragment key={item.id}>
                        <button
                          onClick={() =>
                            isGroup
                              ? setOpenGroups(current => ({ ...current, [item.id]: !isOpen }))
                              : handleNavigation(item.path)
                          }
                          aria-expanded={isGroup ? isOpen : undefined}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-xs font-medium rounded-lg transition-colors ${
                            isActive
                              ? "bg-rp-amber/10 text-rp-amber border-l-[3px] border-rp-amber"
                              : "text-rp-grey hover:bg-rp-separator border-l-[3px] border-transparent"
                          }`}
                        >
                          <Icon className="w-5 h-5 shrink-0" />
                          <span className="flex-1 text-left">{item.label}</span>
                          {item.badge && (
                            <span className="rounded bg-rp-amber/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none text-rp-amber">
                              {item.badge}
                            </span>
                          )}
                          {isGroup && (
                            <ChevronDown aria-hidden="true" className={`h-3.5 w-3.5 shrink-0 opacity-80 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                          )}
                        </button>
                        {isOpen && (
                          <div role="group" aria-label={`${item.label} pages`} className="ml-[15px] space-y-0.5 border-l border-rp-separator pl-[11px]">
                            {(item.children ?? []).map(child => {
                              const childActive = isNavigationChildActive(child, location.pathname);
                              return (
                                <button
                                  key={child.id}
                                  onClick={() => handleNavigation(child.path)}
                                  aria-current={childActive ? "page" : undefined}
                                  className={`w-full truncate rounded-md px-2.5 py-[7px] text-left text-xs transition-colors ${
                                    childActive ? "bg-rp-amber/10 font-semibold text-rp-amber" : "text-gray-400 hover:bg-rp-separator hover:text-rp-grey"
                                  }`}
                                >
                                  {child.label}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </nav>
              </div>

              <div className="flex-1 min-h-[2rem]"></div>

              {/* Bottom Section */}
              <div className="mb-24">
                {/* Role-based Navigation Links */}
                <div className="px-3 py-2 space-y-1">
                  <AlphaModeControl onNavigate={() => setIsOpen(false)} />

                  {isAdmin && !isAdminMode && (
                    <button
                      onClick={() => handleNavigation("/admin/dashboard")}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-xs font-medium rounded-lg transition-colors border border-red-700 text-white hover:bg-red-900/20"
                    >
                      <Crown className="w-5 h-5 shrink-0" />
                      <span>Admin Dashboard</span>
                    </button>
                  )}

                  {isProvider && !isProviderMode && (
                    <button
                      onClick={() => handleNavigation("/provider/dashboard")}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-xs font-medium rounded-lg transition-colors border border-rp-amber text-white hover:bg-rp-amber/20"
                    >
                      <Briefcase className="w-5 h-5 shrink-0" />
                      <span>Provider Dashboard</span>
                    </button>
                  )}

                  {(isAdvisor || isAdmin) && !isAdvisorMode && (
                    <button
                      onClick={() => handleNavigation("/advisor/clients")}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-xs font-medium rounded-lg transition-colors border border-rp-amber text-white hover:bg-rp-amber/20"
                    >
                      <Users className="w-5 h-5 shrink-0" />
                      <span>Advisor Dashboard</span>
                    </button>
                  )}

                  {(isProviderMode || isAdvisorMode || isAdminMode) && isInvestor && (
                    <button
                      onClick={() => handleNavigation("/alpha")}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-xs font-medium rounded-lg transition-colors border border-blue-700 bg-blue-900/20 text-blue-300 hover:bg-blue-800/40"
                    >
                      <Home className="w-5 h-5 shrink-0" />
                      <span>Main Dashboard</span>
                    </button>
                  )}
                </div>

                {/* IBKR Connection Status */}
                <div className="px-3 py-2">
                  <div
                    onClick={() => handleNavigation("/alpha/profile")}
                    className="p-3 rounded-lg cursor-pointer transition-all bg-gray-900 border border-gray-800 hover:bg-gray-800"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-white">
                        IBKR Status
                      </span>
                      <div className="flex items-center space-x-1">
                        <div
                          className={`w-2 h-2 rounded-full ${isIbkrConnected ? "bg-green-400" : "bg-yellow-400"} ${
                            isIbkrConnected ? "animate-pulse" : ""
                          }`}
                        ></div>
                        <span className="text-xs text-rp-grey">
                          {isIbkrConnected ? "Live" : "Pending"}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <img src="/ibkr.png" alt="IBKR" className="w-4 h-4 object-contain" />
                      <span
                        className={`text-xs font-medium ${
                          isIbkrConnected ? "text-green-400" : "text-rp-amber"
                        }`}
                      >
                        {isIbkrConnected ? "Connected" : "Click to Connect"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Bottom Menu Items */}
                <div className="px-3 py-2 space-y-1">
                  {bottomMenuItems.map(item => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;

                    return (
                      <button
                        key={item.id}
                        onClick={() => handleNavigation(item.path)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-xs font-medium rounded-lg transition-colors ${
                          isActive
                            ? "bg-rp-amber/10 text-rp-amber border-l-[3px] border-rp-amber"
                            : "text-rp-grey hover:bg-rp-separator border-l-[3px] border-transparent"
                        }`}
                      >
                        <Icon className="w-5 h-5 shrink-0" />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Logout */}
                <div className="p-3 border-t border-rp-separator space-y-2">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-xs font-medium rounded-lg transition-colors text-red-500 hover:bg-red-500/20"
                  >
                    <LogOut className="w-5 h-5 shrink-0" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileNav;
